// ============================================================================
// TAX MODULE ENDPOINTS (TaxCore ADR-0022)
// ============================================================================
// /api/v1/tax-masters          — authoring (the relocatable tax-master module's contract)
// /api/v1/tax/*                — pack for pickers, preview calculation, schema floor
// /api/v1/tax/charge-mappings  — CRS charge → tax master mapping (effective-dated, append-only)
//
// buildEndpoint / buildEndpointWithQuery add the version prefix, so paths are
// passed raw and path parameters are substituted before the query is built.

import { buildEndpoint, buildEndpointWithQuery } from './base.endpoints';

const MASTERS = '/tax-masters';
const TAX = '/tax';
const CHARGE_MAPPINGS = '/tax/charge-mappings';

const seg = (value: string) => encodeURIComponent(value);

export const taxMasterEndpoints = {
  /** GET /api/v1/tax-masters?jurisdictionCode=&page=&pageSize=&searchTerm=&sortBy=&… (server-side paging) */
  list: (jurisdictionCode: string, query: object = {}) =>
    buildEndpointWithQuery(MASTERS, { jurisdictionCode, ...(query as Record<string, unknown>) }),

  /** GET /api/v1/tax-masters/export?jurisdictionCode=&… — CSV, same query as the list, no paging */
  exportCsv: (jurisdictionCode: string, query: object = {}) =>
    buildEndpointWithQuery(`${MASTERS}/export`, {
      jurisdictionCode, ...(query as Record<string, unknown>), page: undefined, pageSize: undefined,
    }),

  /** GET /api/v1/tax-masters/:rateCode?jurisdictionCode= */
  get: (jurisdictionCode: string, rateCode: string) =>
    buildEndpointWithQuery(`${MASTERS}/${seg(rateCode)}`, { jurisdictionCode }),

  /** POST /api/v1/tax-masters?jurisdictionCode= */
  create: (jurisdictionCode: string) => buildEndpointWithQuery(MASTERS, { jurisdictionCode }),

  /** POST /api/v1/tax-masters/:rateCode/versions?jurisdictionCode= */
  appendVersion: (jurisdictionCode: string, rateCode: string) =>
    buildEndpointWithQuery(`${MASTERS}/${seg(rateCode)}/versions`, { jurisdictionCode }),

  /** PUT / DELETE /api/v1/tax-masters/:rateCode/versions/:effectiveFrom?jurisdictionCode= (date part only) */
  version: (jurisdictionCode: string, rateCode: string, effectiveFrom: string, reason?: string) =>
    buildEndpointWithQuery(
      `${MASTERS}/${seg(rateCode)}/versions/${effectiveFrom.slice(0, 10)}`,
      reason ? { jurisdictionCode, reason } : { jurisdictionCode }),
} as const;

export const taxEndpoints = {
  /** GET /api/v1/tax/entity-jurisdictions — entities and their tax jurisdiction */
  entityJurisdictions: () => buildEndpoint(`${TAX}/entity-jurisdictions`),

  /** GET /api/v1/tax/entity-jurisdictions/options — jurisdictions an entity may be set to */
  entityJurisdictionOptions: () => buildEndpoint(`${TAX}/entity-jurisdictions/options`),

  /** PUT /api/v1/tax/entity-jurisdictions/{entityId} — set or clear (audited) */
  entityJurisdiction: (entityId: string) => buildEndpoint(`${TAX}/entity-jurisdictions/${seg(entityId)}`),

  /** GET /api/v1/tax/pack?jurisdictionCode= (omit for the application's default jurisdiction) */
  pack: (jurisdictionCode?: string) =>
    jurisdictionCode ? buildEndpointWithQuery(`${TAX}/pack`, { jurisdictionCode }) : buildEndpoint(`${TAX}/pack`),

  /** POST /api/v1/tax/calculate — preview; nothing is persisted */
  calculate: () => buildEndpoint(`${TAX}/calculate`),

  /** GET /api/v1/tax/registration/parse?number=&jurisdictionCode=&asOf= (ADR-0023 §1) */
  registrationParse: (number: string, jurisdictionCode?: string, asOf?: string) =>
    buildEndpointWithQuery(`${TAX}/registration/parse`, {
      number, jurisdictionCode: jurisdictionCode || undefined, asOf: asOf || undefined,
    }),

  /** POST /api/v1/tax/component-mix (ADR-0023 §3) */
  componentMix: () => buildEndpoint(`${TAX}/component-mix`),

  /** POST /api/v1/tax/place-of-supply (ADR-0023 §2, engine 2.3.0) */
  placeOfSupply: () => buildEndpoint(`${TAX}/place-of-supply`),

  /** GET /api/v1/tax/schema-floor */
  schemaFloor: () => buildEndpoint(`${TAX}/schema-floor`),

  /** GET /api/v1/tax/readiness?jurisdictionCode= */
  readiness: (jurisdictionCode?: string) =>
    jurisdictionCode ? buildEndpointWithQuery(`${TAX}/readiness`, { jurisdictionCode }) : buildEndpoint(`${TAX}/readiness`),
} as const;

export const chargeTaxMappingEndpoints = {
  /** GET /api/v1/tax/charge-mappings?propertyId=&chargeType=&includeHistory=&page=&pageSize= */
  list: (query: object = {}) => buildEndpointWithQuery(CHARGE_MAPPINGS, query as Record<string, unknown>),

  /** GET /api/v1/tax/charge-mappings/:id */
  get: (id: string) => buildEndpoint(`${CHARGE_MAPPINGS}/${seg(id)}`),

  /** POST /api/v1/tax/charge-mappings */
  create: () => buildEndpoint(CHARGE_MAPPINGS),

  /** POST /api/v1/tax/charge-mappings/:id/close */
  close: (id: string) => buildEndpoint(`${CHARGE_MAPPINGS}/${seg(id)}/close`),
} as const;

export default taxEndpoints;
